<script lang="ts">
  import { deleteWhenSearchQueryStringParameter, setQueryStringParameter } from "../../url";
  import { SearchState } from "../../services/search-service";
  import type { FormatType } from "../../models/when-search.interface";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";
  import type {SearchStateModel} from "../../models/search.interface";

  export let format: FormatType = "";
  export let minDate: string | undefined = undefined;
  export let maxDate: string | undefined = undefined;
  export let enabled: boolean = true;

  let checked: boolean = false;
  let startDate: string = "";
  let endDate: string = "";
  let startInput: string;
  let endInput: string;
  let statusMessage: string = "";

  let startError: string | null = null;
  let endError: string | null = null;

  SearchState.subscribe((search: SearchStateModel) => {
    startDate = search.params.get("startDate");
    startInput = format === "Date" && startDate ? startDate.split("T")[0] : trimToMinute(startDate);

    endDate = search.params.get("endDate");
    endInput = format === "Date" && endDate ? endDate.split("T")[0] : trimToMinute(endDate);
  })

  $: if (checked && format === "DateTime") {
    // This reactive statement will run whenever checked becomes true
    endInput = trimToMinute(endDate);
  }

  // Single source of validation for both date fields.
  function validate(
    formattedStart: string,
    formattedEnd: string,
    field: "start" | "end"
  ): string | null {
    const candidate = field === "start" ? formattedStart : formattedEnd;

    if (!isRealCalendarDate(candidate)) {
      return field === "start"
        ? "Start date is not a valid calendar date."
        : "End date is not a valid calendar date.";
    }

    if (!isDateRangeValid(formattedStart, formattedEnd)) {
      return field === "start"
        ? "Start date must be on or before the end date."
        : "End date must be on or after the start date.";
    }

    if (!isDateWithinRange(candidate, minDate, maxDate, format)) {
      const lo = minDate?.split("T")[0];
      const hi = maxDate?.split("T")[0];
      return lo && hi
        ? `Date is outside the available range (${lo} – ${hi}).`
        : "Date is outside the available range.";
    }

    return null;
  }


  function isRealCalendarDate(formatted: string): boolean {
    const [y, m, d] = formatted.split("T")[0].split("-").map(Number);
    if (!y || !m || !d) return false;
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
  }

  function nativeMessage(input: HTMLInputElement): string | null {
    const v = input.validity;
    if (v.badInput) return "Enter a valid date.";
    if (v.rangeUnderflow) return `Date is before the earliest available (${minDate?.split("T")[0]}).`;
    if (v.rangeOverflow) return `Date is after the latest available (${maxDate?.split("T")[0]}).`;
    return null;
  }

  function setStartDate(input: HTMLInputElement) {
    const native = nativeMessage(input);
    if (native) {
      startError = native;
      return;
    }

    const startDateInput = input.value;
    if (!startDateInput) {
      startError = null;
      return;
    }

    const formattedStartDate = formatDateTime(startDateInput, format);
    const autoFillEnd = !checked || !endDate;
    const formattedEndDate = autoFillEnd
      ? formatDateTime(startDateInput, format, true)
      : endDate;

    // Validate before writing anything, so invalid input never triggers a search.
    startError = validate(formattedStartDate, formattedEndDate, "start");
    if (startError) return;

    if (autoFillEnd) {
      setQueryStringParameter("endDate", formattedEndDate);
      endInput =
        format === "Date" ? formattedEndDate.split("T")[0] : trimToMinute(formattedEndDate);
    }

    setQueryStringParameter("startDate", formattedStartDate);
    startInput =
      format === "Date" ? formattedStartDate.split("T")[0] : trimToMinute(formattedStartDate);
  }

  function setEndDate(input: HTMLInputElement) {
    const native = nativeMessage(input);
    if (native) {
      endError = native;
      return;
    }

    const endDateInput = input.value;
    if (!endDateInput) {
      endError = null;
      return;
    }

    const formattedEndDate = formatDateTime(endDateInput, format, true);
    endError = validate(startDate, formattedEndDate, "end");
    if (endError) return;

    setQueryStringParameter("endDate", formattedEndDate);
    endInput = format === "Date" ? formattedEndDate.split("T")[0] : trimToMinute(formattedEndDate);
  }

  // Clear all date selections and query parameters
  function clearDates() {
    startInput = "";
    endInput = "";
    startError = null;
    endError = null;
    deleteWhenSearchQueryStringParameter("startDate", "endDate");
  }

  // Validate that a date is within the min and max date range
  function isDateWithinRange(
    date: string,
    minDate: string | undefined,
    maxDate: string | undefined,
    format: FormatType
  ): boolean {
    // Skip validation for Month-based formats
    if (format === "Month" || format === "Month/Day" || format === "Month/Day/Hour") {
      return true;
    }

    // Skip validation if either bound is missing
    if (!minDate || !maxDate) {
      return true;
    }

    let selectedDate = new Date(date);
    let min = new Date(minDate);
    let max = new Date(maxDate);

    return selectedDate >= min && selectedDate <= max;
  }

  // Validate that the start date is before the end date
  function isDateRangeValid(start: string, end: string): boolean {
    if (!start || !end) return false;

    const startDateTime = new Date(start);
    const endDateTime = new Date(end);

    if (startDateTime < endDateTime) return true;
    if (startDateTime.getTime() !== endDateTime.getTime()) return false;

    // If dates are same, check hours and minutes
    const startParts = start.split("T")[1].split(":");
    const endParts = end.split("T")[1].split(":");

    const startHour = parseInt(startParts[0], 10);
    const endHour = parseInt(endParts[0], 10);

    if (startHour < endHour) return true;
    if (startHour > endHour) return false;

    const startMinute = parseInt(startParts[1], 10);
    const endMinute = parseInt(endParts[1], 10);

    return startMinute <= endMinute;
  }

  function trimToMinute(date: string): string {
    if (!date) return "";
    return date.split(":").slice(0, 2).join(":");
  }

  // Format a date string based on the type and whether it's an end date
  function formatDateTime(date: string, type: FormatType, isEnd: boolean = false): string {
    if (!date) return "";

    // Remove any existing time component to start fresh
    let baseDate = date.split("T")[0];

    // For Date format, always return the date with midnight time
    if (type === "Date") {
      return `${baseDate}T${isEnd ? "23:59:59" : "00:00:00"}`;
    }

    // For DateTime format
    else if (type === "DateTime") {
      // If there's a time component in the input, preserve hours and minutes
      if (date.includes("T")) {
        const timeParts = date.split("T")[1].split(":");
        const hours = timeParts[0] || "00";
        const minutes = timeParts[1] || "00";
        return `${baseDate}T${hours}:${minutes}${isEnd ? ":59" : ":00"}`;
      } else {
        // If no time provided, use default times
        return `${baseDate}T${isEnd ? "23:59:59" : "00:00:00"}`;
      }
    }

    // For other formats (Month, Month/Day, Month/Day/Hour)
    return `${baseDate}T${isEnd ? "23:59:59" : "00:00:00"}`;
  }

  function handleDateRangeChange() {
    if (checked) {
      statusMessage = "End Date field added below.";
      if (startDate) {
        const currentEndDate = endDate || formatDateTime(startDate, format, true);
        endInput = format === "Date" ? currentEndDate.split("T")[0] : trimToMinute(currentEndDate);
        if (!endDate) {
          setQueryStringParameter("endDate", currentEndDate);
        }
      }
    } else {
      statusMessage = "End Date field removed.";
      endError = null;
    }

    // Clear status message after announcement
    setTimeout(() => {
      statusMessage = "";
    }, 1000);
  }
</script>

<li class="mb-1">
  <form>
    <fieldset>
      <legend class="mb-2 fs-5">
        When
        <!-- tooltip icon -->
        <TooltipHybridButton
          searchUrlQueryParam="when"
          tooltip="
          <p>
            Enter a single date, or click the Select Date Range option to enter both a start date
            and end date.
          </p>
          <p>
            Date Format: <b> {format} </b>
          </p>"
        />
      </legend>

      <!-- Start Date Inputs -->
      <div class="form-floating margin-2">
        <input
          {...{ type: format === "Date" ? "date" : "datetime-local" }}
          class="form-control"
          class:is-invalid={!!startError}
          id="floatingInputStartDate"
          placeholder="Start Date"
          disabled={!enabled}
          bind:value={startInput}
          min={minDate?.split("T")[0]}
          max={maxDate?.split("T")[0]}
          aria-invalid={!!startError}
          aria-describedby={startError ? "startDateError" : undefined}
          on:blur={(e) => {
            setStartDate(e.currentTarget);
          }}
          on:keydown={(e) => {
            if (e.key === "Enter") {
              setStartDate(e.currentTarget);
            }
          }}
        />
        <label for="floatingInputStartDate">Start Date</label>
        {#if startError}
          <div id="startDateError" class="invalid-feedback">{startError}</div>
        {/if}
      </div>

      <!-- Toggle Date Range Selection -->
      <div class="form-check mt-2">
        <input
          class="form-check-input"
          type="checkbox"
          bind:checked
          id="date-range-check"
          on:mouseenter={() => {
            if (endDate) {
              endInput = format === "Date" ? endDate.split("T")[0] : trimToMinute(endDate);
            }
          }}
          on:change={handleDateRangeChange}
        />
        <label class="form-check-label" for="date-range-check"> Select Date Range </label>
      </div>

      <!-- End Date Inputs - MOVED AFTER CHECKBOX -->
      {#if checked}
        <div class="form-floating mt-2">
          <input
            {...{ type: format === "Date" ? "date" : "datetime-local" }}
            class="form-control"
            class:is-invalid={!!endError}
            id="floatingInputEndDate"
            placeholder="End Date"
            bind:value={endInput}
            disabled={!enabled}
            min={minDate?.split("T")[0]}
            max={maxDate?.split("T")[0]}
            aria-invalid={!!endError}
            aria-describedby={endError ? "endDateError" : undefined}
            on:blur={(e) => {
              setEndDate(e.currentTarget);
            }}
            on:keydown={(e) => {
              if (e.key === "Enter") {
                setEndDate(e.currentTarget);
              }
            }}
          />
          <label for="floatingInputEndDate">End Date</label>
          {#if endError}
            <div id="endDateError" class="invalid-feedback">{endError}</div>
          {/if}
        </div>
      {/if}
    </fieldset>
  </form>

  <!-- ARIA live region for status messages -->
  <div aria-live="polite" aria-atomic="true" class="visually-hidden">
    {statusMessage}
  </div>

  <!-- Display Start and End Dates with Time -->
  {#if startDate && endDate}
    <div class="d-flex py-1">
      <span
        style="overflow-x: hidden; text-overflow: ellipsis"
        class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1"
        role="status"
        aria-label="when search start date search pill: {startDate}"
      >
        <button type="button" class="btn p-0 m-0" on:click={clearDates}>
          <i class="bi bi-x text-bg-dark" aria-label="Delete {startDate}"></i>
        </button>
        Start date: {startDate}
      </span>
    </div>
    <div class="d-flex py-1">
      <span
        style="overflow-x: hidden; text-overflow: ellipsis"
        class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1"
        role="status"
        aria-label="when search end date search pill: {endDate}"
      >
        <button type="button" class="btn p-0 m-0" on:click={clearDates}>
          <i class="bi bi-x text-bg-dark" aria-label="Delete {endDate}"></i>
        </button>
        End date: {endDate}
      </span>
    </div>
  {/if}
</li>

<style>
  .invalid-feedback {
    font-size: 0.75rem;
  }
</style>
